/* eslint-disable no-nested-ternary */
import * as React from 'react';
import Axios from 'axios';
import _, { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { Modal, Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import type { CachedOrg } from '@bb/automaton';
import {
  AppointmentStatus,
  CovidPreferences,
  CovidFormStatus,
  JsonObject,
} from '@bb/domain/dist/types';
import { AnsweringOrgController } from 'api';

import errorReporter from '@app/errorReporter';
import { IGetOrgsByGroupResponse } from 'api/AnsweringOrgController/getOrgsByGroup';
import { AnsweringOrgGroupDoc } from '@bb/domain/dist/schemas';
import Loading from '../../react-common/Loading';
import { QueuePreferences, AppointmentPreferences } from './index';
import Empty from '../../react-common/Empty';

interface QueuePreferencesModalProps {
  closeModal: () => void;
  onFetchPreferences: (
    queuePreferences: QueuePreferences,
    allGroupedOrgsMap: Record<string, true | undefined>,
  ) => void;
  onSavePreferences: () => void;
  onResetPreferences: () => void;
  visible: boolean;
  $rootScope: any;
}

interface QueuePreferencesModalState {
  currentTab: number;
  groups: JsonObject<
    Omit<AnsweringOrgGroupDoc, 'orgs' | '_id' | 'createdAt' | 'updatedAt'> & {
      orgs: Pick<CachedOrg, 'id' | 'locations' | 'name'>[];
    }
  >[];
  loading: boolean;
  loadingOrgs: boolean;
  pendingSave: boolean;
  pendingReset: boolean;
  preferences: QueuePreferences;
  groupCollapseStates: { [groupId: string]: boolean };
  orgCollapseStates: { [orgId: string]: boolean };
  unsavedPreferences: QueuePreferences;
  /** Object containing all org ids listed in groupPreferences */
  allGroupedOrgsMap: Record<string, true | undefined>;
  paginationData: {
    skip: number;
    limit: number;
    limitMult: number;
    limitMax: number;
    hasMore: boolean;
  };
}

type TogglePreferenceArgs<T> = T &
  Omit<
    {
      groupId?: undefined;
      orgId?: undefined;
      locationIndex?: undefined;
      status?: undefined;
      covidStatus?: undefined;
      additional?: undefined;
    },
    keyof T
  >;

const TABS: { id: string; label: string }[] = [
  { id: 'group-preferences', label: 'VIEW BROKERAGE QUEUE' },
  { id: 'appointment-preferences', label: 'VIEW SHOWING TYPE' },
];

const DISPLAY_STATUSES: Record<AppointmentStatus, string> = {
  config_change: 'Config Change',
  time_change: 'Time Change',
  time_passed: 'Time Passed',
  attempted: 'Attempted',
  cancel: 'Cancelled',
  confirm: 'Confirmed',
  deny: 'Denied',
  new: 'New',
  removed: 'Removed',
  suggested_time_change: 'Suggested Time Change', // Unused
  historical: 'Historical', // Unused
  non_agent_approval: 'Non Agent Approval', // Unused
  draft: 'Draft',
};

const DISPLAY_COVID: Record<CovidFormStatus, string> = {
  covid19Signed: 'Completed Covid-19 Form',
  covid19Unsigned: 'Unsigned Covid-19 Form',
};

class QueuePreferencesModal extends React.Component<
  QueuePreferencesModalProps,
  QueuePreferencesModalState
> {
  static propTypes: PropTypes.ValidationMap<QueuePreferencesModalProps>;

  constructor(props: QueuePreferencesModalProps) {
    super(props);
    this.state = {
      currentTab: 0,
      loading: true,
      loadingOrgs: true,
      pendingSave: false,
      pendingReset: false,
      groups: [],
      preferences: {
        appointmentPreferences: {} as AppointmentPreferences,
        groupPreferences: {},
        covidPreferences: {} as CovidPreferences,
        additionalShowingPreferences: {},
      },
      groupCollapseStates: {},
      orgCollapseStates: {},
      unsavedPreferences: {
        appointmentPreferences: {} as AppointmentPreferences,
        groupPreferences: {},
        covidPreferences: {} as CovidPreferences,
        additionalShowingPreferences: {},
      },
      allGroupedOrgsMap: {},
      // Fetch brokerages in chunks of limit -> limit * limitMult -> limit * limitMult^2 -> ... -> limitMax
      paginationData: {
        skip: 0,
        limit: 20,
        limitMult: 4,
        limitMax: 2500,
        hasMore: true,
      },
    };
  }

  public componentDidMount() {
    this.fetchData();
  }

  public fetchData = async () => {
    try {
      await this.fetchPreferences();
      await this.fetchPaginatedBrokerages();
    } catch (err) {
      errorReporter.error(err);
    } finally {
      this.setState({ loading: false });
    }
  };

  public fetchPaginatedBrokerages = async () => {
    try {
      const { paginationData } = this.state;
      const { skip, limit, limitMult, limitMax, hasMore } = paginationData;
      if (!hasMore) return;

      const result = await AnsweringOrgController.getOrgsByGroup(
        String(skip),
        String(limit),
      );

      const newGroups = this.populateGroupsFromGroupedOrgs(result);
      const newPreferences = this.populateDefaultGroupPreferences(result);

      this.setState({
        preferences: newPreferences,
        groups: newGroups,
        paginationData: {
          ...paginationData,
          skip: skip + limit,
          limit: Math.min(limitMax, limit * limitMult),
          hasMore: result.hasMore,
        },
        loadingOrgs: false,
      });
    } catch (err) {
      errorReporter.error(`Error fetching paginated brokerages: ${err}`);
    }
  };

  private populateGroupsFromGroupedOrgs = ({
    groups,
    notGroupedOrgs,
  }: IGetOrgsByGroupResponse) => {
    const { $rootScope } = this.props;
    const { groups: currentGroups } = this.state;
    if (groups.length) {
      groups.forEach(fetchedGroup => {
        const foundGroup = currentGroups.find(
          group => group.id === fetchedGroup._id,
        );
        if (foundGroup) {
          foundGroup.orgs.push(...fetchedGroup.orgs);
        } else {
          currentGroups.push({ ...fetchedGroup, id: fetchedGroup._id });
        }
      });
    }

    if (notGroupedOrgs.length) {
      const noGroup = currentGroups.find(group => group.id === 'noGroup');
      if (!noGroup) {
        currentGroups.push({
          id: 'noGroup',
          label: 'Non-Grouped Clients',
          orgs: notGroupedOrgs,
          owner: $rootScope.auth.org.id,
        });
      } else {
        noGroup.orgs.push(...notGroupedOrgs);
      }
    }

    return currentGroups;
  };

  private populateDefaultGroupPreferences = ({
    groups,
    notGroupedOrgs,
  }: IGetOrgsByGroupResponse) => {
    const { allGroupedOrgsMap, preferences } = this.state;
    const { groupPreferences } = preferences;
    if (!preferences.groupPreferences.noGroup) {
      preferences.groupPreferences.noGroup = {};
    }

    if (groups.length) {
      groups.forEach(fetchedGroup => {
        if (!groupPreferences[fetchedGroup._id]) {
          groupPreferences[fetchedGroup._id] = {};
        }
        fetchedGroup.orgs.forEach(org => {
          if (!groupPreferences[fetchedGroup._id][org.id]) {
            groupPreferences[fetchedGroup._id][org.id] = new Array(
              org.locations.length,
            ).fill(true);
          }
        });
      });
    }

    if (notGroupedOrgs.length) {
      notGroupedOrgs.forEach(org => {
        // Ignore any orgs that have already been populated from inital preference fetch
        if (allGroupedOrgsMap[org.id]) return;
        if (groupPreferences.noGroup[org.id]) return;
        groupPreferences.noGroup[org.id] = new Array(org.locations.length).fill(
          true,
        );
      });
    }

    return preferences;
  };

  public fetchPreferences = async () => {
    try {
      const { preferences, allGroupedOrgsMap } = this.state;
      const { onFetchPreferences } = this.props;
      const {
        data: {
          appointmentPreferences,
          groupPreferences,
          covidPreferences,
          additionalShowingPreferences,
        },
      } = await Axios.get<QueuePreferences>('/answering/queue_preferences');
      preferences.appointmentPreferences = _.merge(
        {},
        preferences.appointmentPreferences,
        appointmentPreferences,
      );

      Object.entries(groupPreferences).forEach(
        ([groupId, orgIdToBranchToggles]) => {
          if (!preferences.groupPreferences[groupId]) {
            preferences.groupPreferences[groupId] = {};
          }
          Object.entries(orgIdToBranchToggles).forEach(
            ([orgId, branchToggles]) => {
              preferences.groupPreferences[groupId][orgId] = branchToggles;
            },
          );
        },
      );
      preferences.covidPreferences = _.merge(
        {},
        preferences.covidPreferences,
        covidPreferences,
      );
      preferences.additionalShowingPreferences = _.merge(
        {},
        preferences.additionalShowingPreferences,
        additionalShowingPreferences,
      );
      let newAllGroupedOrgsMap = allGroupedOrgsMap;
      if (preferences.groupPreferences) {
        const allGroupedOrgIds = Object.values(preferences.groupPreferences)
          .map(orgLocationMap => Object.keys(orgLocationMap))
          .flat(1);
        newAllGroupedOrgsMap = allGroupedOrgIds.reduce<
          typeof allGroupedOrgsMap
        >((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {});
      }
      this.setState(
        { preferences, allGroupedOrgsMap: newAllGroupedOrgsMap },
        () => {
          onFetchPreferences(preferences, newAllGroupedOrgsMap);
        },
      );
    } catch (err) {
      errorReporter.error(`Error getting preferences: ${err}`);
    }
  };

  public savePreferences = async () => {
    try {
      this.setState({ pendingSave: true });
      const { preferences } = this.state;
      const { onSavePreferences } = this.props;
      await Axios.post('/answering/queue_preferences', preferences);
      this.setState(
        { pendingSave: false, unsavedPreferences: preferences },
        () => {
          onSavePreferences();
        },
      );
    } catch (err) {
      errorReporter.error(`Error saving preferences: ${err}`);
      this.setState({ pendingSave: false });
    }
  };

  public resetPreferences = async () => {
    try {
      this.setState({ pendingReset: true });

      const { onResetPreferences } = this.props;

      const { data: defaultPreferences } = await Axios.delete(
        '/answering/queue_preferences',
      );
      if (!defaultPreferences.groupPreferences) {
        // no groups have been created yet
        defaultPreferences.groupPreferences = {};
      }
      this.setState(
        { preferences: defaultPreferences, pendingReset: false },
        () => {
          onResetPreferences();
        },
      );
    } catch (err) {
      errorReporter.error(`Error resetting preferences: ${err}`);
      this.setState({ pendingReset: false });
    }
  };

  public togglePreference = ({
    groupId,
    orgId,
    locationIndex,
    status,
    covidStatus,
    additional,
    type,
  }:
    | TogglePreferenceArgs<{ type: 'appointment'; status: AppointmentStatus }>
    | TogglePreferenceArgs<{
        type: 'group';
        groupId: string;
        orgId?: undefined;
        locationIndex?: undefined;
      }>
    | TogglePreferenceArgs<{
        type: 'group';
        groupId: string;
        orgId: string;
        locationIndex?: undefined;
      }>
    | TogglePreferenceArgs<{
        type: 'group';
        groupId: string;
        orgId: string;
        locationIndex: number;
      }>
    | TogglePreferenceArgs<{ type: 'covid'; covidStatus: CovidFormStatus }>
    | TogglePreferenceArgs<{
        type: 'additional';
        additional: keyof QueuePreferences['additionalShowingPreferences'];
      }>) => {
    const { preferences } = this.state;
    const {
      appointmentPreferences,
      groupPreferences,
      covidPreferences,
      additionalShowingPreferences,
    } = preferences;
    if (type === 'group') {
      if (groupPreferences[groupId]) {
        if (locationIndex !== undefined) {
          if (groupPreferences[groupId][orgId]) {
            const locationKey =
              typeof locationIndex === 'string'
                ? parseInt(locationIndex, 10)
                : locationIndex;
            if (
              Object.hasOwnProperty.call(
                groupPreferences[groupId][orgId],
                locationIndex,
              )
            ) {
              groupPreferences[groupId][orgId][locationKey] =
                !groupPreferences[groupId][orgId][locationKey];
            }
          }
        } else if (orgId !== undefined) {
          const updateValue = Object.entries(
            groupPreferences[groupId][orgId],
          ).some(([__, value]) => !value);
          Object.keys(groupPreferences[groupId][orgId]).forEach(key => {
            groupPreferences[groupId][orgId][parseInt(key, 10)] = updateValue;
          });
        } else {
          const updateValue = Object.values(groupPreferences[groupId])
            .map(org => org.some(value => !value))
            .some(result => result);
          Object.keys(groupPreferences[groupId]).forEach(key => {
            Object.keys(groupPreferences[groupId][key]).forEach(k => {
              groupPreferences[groupId][key][parseInt(k, 10)] = updateValue;
            });
          });
        }
      }
    }

    if (type === 'appointment') {
      appointmentPreferences[status] = !appointmentPreferences[status];
    }

    if (type === 'covid') {
      covidPreferences[covidStatus] = !covidPreferences[covidStatus];
    }

    if (type === 'additional') {
      additionalShowingPreferences[additional!] =
        !additionalShowingPreferences[additional!];
    }

    this.setState({ preferences });
  };

  public toggleCollapsed = <A extends 'group' | 'org'>(
    accessorId: string,
    accessor: A,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const key = `${accessor}CollapseStates` as const;
    const collapseStates = this.state[key];
    collapseStates[accessorId] = !collapseStates[accessorId];
    this.setState({ [key]: collapseStates } as Record<
      typeof key,
      QueuePreferencesModalState[typeof key]
    >);
    // If collapsed check if we need to re-fetch brokerages
    if (collapseStates[accessorId] === true) {
      const { offsetHeight, scrollHeight, scrollTop } = e.currentTarget
        .offsetParent! as HTMLDivElement;
      this.handleScroll({ offsetHeight, scrollHeight, scrollTop });
    }
  };

  public expandAll = () => {
    const { orgCollapseStates, groupCollapseStates, groups } = this.state;

    groups.forEach(group => {
      groupCollapseStates[group.id] = false;
      group.orgs.forEach(org => {
        orgCollapseStates[org.id] = false;
      });
    });

    this.setState({ orgCollapseStates, groupCollapseStates });
  };

  public collapseAll = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const { orgCollapseStates, groupCollapseStates, groups } = this.state;

    groups.forEach(group => {
      groupCollapseStates[group.id] = true;
      group.orgs.forEach(org => {
        orgCollapseStates[org.id] = true;
      });
    });
    this.setState({ orgCollapseStates, groupCollapseStates });
    const { offsetHeight, scrollHeight, scrollTop } = e.currentTarget
      .offsetParent! as HTMLDivElement;
    // Check if we need to re-fetch brokerages
    this.handleScroll({ offsetHeight, scrollHeight, scrollTop });
  };

  public changeCurrentTab = (newTab: number) =>
    this.setState({ currentTab: newTab });

  private _handleShow = () =>
    this.setState({ unsavedPreferences: _.cloneDeep(this.state.preferences) });

  private _handleClose = () =>
    this.setState(
      { preferences: _.cloneDeep(this.state.unsavedPreferences) },
      () => this.props.closeModal(),
    );

  private handleScroll = debounce(
    ({
      offsetHeight,
      scrollHeight,
      scrollTop,
    }: {
      offsetHeight: number;
      scrollHeight: number;
      scrollTop: number;
    }) => {
      const { paginationData, loadingOrgs } = this.state;
      if (!paginationData.hasMore || loadingOrgs) return;
      const bottom = scrollHeight;
      const y = offsetHeight + scrollTop;

      // scrollTop === 0 means all the content is within view
      if (scrollTop === 0 || y >= bottom * 0.8) {
        this.setState({ loadingOrgs: true }, this.fetchPaginatedBrokerages);
      }
    },
    200,
  );

  public render() {
    const {
      currentTab,
      groupCollapseStates,
      groups,
      loading,
      loadingOrgs,
      orgCollapseStates,
      pendingSave,
      pendingReset,
      preferences: {
        appointmentPreferences,
        groupPreferences,
        covidPreferences,
        additionalShowingPreferences,
      },
      paginationData: { hasMore },
    } = this.state;
    const { visible } = this.props;
    return (
      <Modal
        data-cy="modal-popup"
        show={visible}
        onShow={this._handleShow}
        onHide={this._handleClose}
      >
        <div className="color-line"></div>
        <Modal.Header closeButton style={{ textAlign: 'center' }}>
          <Modal.Title>Edit Queue Preferences</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{ maxHeight: '65vh', minHeight: '25vh', overflowY: 'scroll' }}
          onScroll={
            hasMore && !loadingOrgs
              ? e => {
                  // @ts-expect-error The type on react-bootstrap is likely wrong
                  const { offsetHeight, scrollHeight, scrollTop } =
                    e.nativeEvent.target!;
                  this.handleScroll({ offsetHeight, scrollHeight, scrollTop });
                }
              : undefined
          }
        >
          <>
            {loading ? (
              <Loading size="fa-5x" />
            ) : groups.length > 0 ? (
              <>
                <div className="preferences-tab-container">
                  <div className="btn-group listing-edit-nav">
                    {TABS.map(({ id, label }, index) => (
                      <button
                        key={id}
                        className={`btn ${currentTab === index && 'active'}`}
                        type="button"
                        onClick={() => this.changeCurrentTab(index)}
                        {...(currentTab === index
                          ? { style: { backgroundColor: '#175A87' } }
                          : {})}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <hr />
                </div>
                {currentTab === 0 && (
                  <div className="preferences-actions-container">
                    <div className="preferences-actions">
                      <button
                        className="btn btn-default preferences-action"
                        onClick={this.expandAll}
                      >
                        Expand All
                      </button>
                      <button
                        className="btn btn-default preferences-action"
                        onClick={this.collapseAll}
                      >
                        Collapse All
                      </button>
                    </div>
                  </div>
                )}
                <div className="preferences-content">
                  {currentTab === 0 &&
                    groups.map(group => (
                      <div
                        key={group.id}
                        className="group-preferences-container"
                      >
                        <div className="group-parent-container">
                          <div
                            className="collapse-toggle"
                            onClick={e =>
                              this.toggleCollapsed(group.id, 'group', e)
                            }
                          >
                            {groupCollapseStates[group.id] ? (
                              <i className="fa fa-caret-right"></i>
                            ) : (
                              <i className="fa fa-caret-down"></i>
                            )}
                          </div>
                          <div
                            className="input-group"
                            onClick={() =>
                              this.togglePreference({
                                groupId: group.id,
                                type: 'group',
                              })
                            }
                          >
                            <input
                              type="checkbox"
                              checked={
                                groupPreferences[group.id] &&
                                Object.values(groupPreferences[group.id])
                                  .map(org => org.some(value => value))
                                  .some(result => result)
                              }
                              readOnly
                              className="parent-preference-checkbox"
                            />
                            <label>&nbsp;{group.label}</label>
                          </div>
                        </div>
                        {!groupCollapseStates[group.id] &&
                          group.orgs.map(org => (
                            <div
                              key={org.id}
                              className="org-preferences-container"
                            >
                              <div className="org-parent-container">
                                <div
                                  className="collapse-toggle"
                                  onClick={e =>
                                    this.toggleCollapsed(org.id, 'org', e)
                                  }
                                >
                                  {orgCollapseStates[org.id] ? (
                                    <i className="fa fa-caret-right"></i>
                                  ) : (
                                    <i className="fa fa-caret-down"></i>
                                  )}
                                </div>
                                <div
                                  className="input-group"
                                  onClick={() =>
                                    this.togglePreference({
                                      groupId: group.id,
                                      orgId: org.id,
                                      type: 'group',
                                    })
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      groupPreferences[group.id] &&
                                      groupPreferences[group.id][org.id] &&
                                      Object.keys(
                                        groupPreferences[group.id][org.id],
                                      ).some(
                                        key =>
                                          groupPreferences[group.id][org.id][
                                            parseInt(key, 10)
                                          ],
                                      )
                                    }
                                    readOnly
                                    className="parent-preference-checkbox"
                                  />
                                  <label>&nbsp;{org.name}</label>
                                </div>
                              </div>
                              {!orgCollapseStates[org.id] && (
                                <div className="org-location-preferences">
                                  {org.locations.map(
                                    (location, index) =>
                                      !location.isDeleted && (
                                        <div
                                          key={index}
                                          className="input-group"
                                          onClick={() =>
                                            this.togglePreference({
                                              groupId: group.id,
                                              orgId: org.id,
                                              locationIndex: index,
                                              type: 'group',
                                            })
                                          }
                                        >
                                          <input
                                            type="checkbox"
                                            checked={
                                              groupPreferences[group.id] &&
                                              groupPreferences[group.id][
                                                org.id
                                              ] &&
                                              groupPreferences[group.id][
                                                org.id
                                              ][index]
                                            }
                                            readOnly
                                          />
                                          <label>&nbsp;{location.name}</label>
                                        </div>
                                      ),
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    ))}
                  {currentTab === 0 && loadingOrgs && (
                    <>
                      <Row className="justify-content-md-center">
                        <Col>
                          <Loading size="fa-3x" />
                        </Col>
                      </Row>
                      <div
                        style={{
                          textAlign: 'center',
                          textTransform: 'initial',
                        }}
                      >
                        Loading Brokerages...
                      </div>
                    </>
                  )}
                  {/* This button is here just in case the infinite scroller doesn't fire due to the fetched brokerages being rendered in a collapsed state */}
                  {currentTab === 0 && !loadingOrgs && hasMore && (
                    <button
                      type="button"
                      className="btn btn-default btn-block"
                      style={{ textTransform: 'initial', marginTop: '8px' }}
                      onClick={() => {
                        this.setState(
                          { loadingOrgs: true },
                          this.fetchPaginatedBrokerages,
                        );
                      }}
                    >
                      Load More Brokerages
                    </button>
                  )}
                  {currentTab === 1 && (
                    <div className="appointment-type-container">
                      <StyledFilterTypeContainer>
                        {Object.entries(appointmentPreferences).map(
                          ([status, bool]: [AppointmentStatus, boolean]) => (
                            <div
                              className="input-group"
                              key={status}
                              onClick={() =>
                                this.togglePreference({
                                  status,
                                  type: 'appointment',
                                })
                              }
                            >
                              <input
                                type="checkbox"
                                checked={bool}
                                readOnly
                                className="parent-preference-checkbox"
                              />
                              <label>&nbsp;{DISPLAY_STATUSES[status]}</label>
                            </div>
                          ),
                        )}
                        <br />
                        <label>Display Status Filter</label>
                        <div
                          className="input-group"
                          onClick={() =>
                            this.togglePreference({
                              additional: 'mustCall',
                              type: 'additional',
                            })
                          }
                        >
                          <input
                            type="checkbox"
                            checked={additionalShowingPreferences.mustCall}
                            readOnly
                            className="parent-preference-checkbox"
                          />
                          <label>&nbsp;Must Call</label>
                        </div>
                      </StyledFilterTypeContainer>
                      <StyledFilterTypeContainer>
                        <label>Covid-19 Filter</label>
                        {Object.entries(covidPreferences).map(
                          ([covidStatus, bool]: [CovidFormStatus, boolean]) => (
                            <div
                              className="input-group"
                              key={covidStatus}
                              onClick={() =>
                                this.togglePreference({
                                  covidStatus,
                                  type: 'covid',
                                })
                              }
                            >
                              <input
                                type="checkbox"
                                checked={bool}
                                readOnly
                                className="parent-preference-checkbox"
                              />
                              <label className="covid-preferences-label">
                                &nbsp;{DISPLAY_COVID[covidStatus]}
                              </label>
                            </div>
                          ),
                        )}
                        <br />
                        <label>Alternative Filter</label>
                        <div
                          className="input-group"
                          onClick={() =>
                            this.togglePreference({
                              additional: 'customContact',
                              type: 'additional',
                            })
                          }
                        >
                          <input
                            type="checkbox"
                            checked={additionalShowingPreferences.customContact}
                            readOnly
                            className="parent-preference-checkbox"
                          />
                          <StyledNonTransformLabel>
                            Custom Contact Booked Showings
                          </StyledNonTransformLabel>
                        </div>
                        <div
                          className="input-group"
                          onClick={() =>
                            this.togglePreference({
                              additional: 'homeInspection',
                              type: 'additional',
                            })
                          }
                        >
                          <input
                            type="checkbox"
                            checked={
                              additionalShowingPreferences.homeInspection
                            }
                            readOnly
                            className="parent-preference-checkbox"
                          />
                          <StyledNonTransformLabel>
                            &nbsp;Home Inspection
                          </StyledNonTransformLabel>
                        </div>
                      </StyledFilterTypeContainer>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Empty
                height={300}
                text="There are no groups within your organization to display"
              />
            )}
          </>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-default"
            onClick={this._handleClose}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-outline btn-danger"
            disabled={pendingSave || pendingReset || groups.length === 0}
            onClick={this.resetPreferences}
            style={{ borderColor: '#841C10', color: '#841C10' }}
          >
            {pendingReset ? <>Resetting...</> : <>Reset</>}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={pendingSave || pendingReset || groups.length === 0}
            onClick={this.savePreferences}
          >
            {pendingSave ? (
              <>
                <i className="fa fa-cog fa-spin"></i>
                <span> RESOLVING...</span>
              </>
            ) : (
              <>
                <i className="fa fa-save"></i>
                <span> Save</span>
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}

QueuePreferencesModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  onFetchPreferences: PropTypes.func.isRequired,
  onSavePreferences: PropTypes.func.isRequired,
  onResetPreferences: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
};

export default QueuePreferencesModal;

const StyledFilterTypeContainer = styled.div`
  width: 49%;
`;

const StyledNonTransformLabel = styled.label`
  && {
    text-transform: none;
  }
`;
